import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { useBrandKits } from '@/hooks/useBrandKits';
import type { AdCritiqueResult } from '@/lib/concept-workbench/ad-critique';

export interface ConceptAdCritiqueTarget {
  conceptId: string | null | undefined;
  /** `concept_renditions.id` of the selected size. */
  renditionId: string | null | undefined;
  /** The copy option the Content section has selected. Absent: the server picks the current one. */
  adUnitId?: string | null;
  /** The selected size's effective image. */
  mediaId?: string | null;
  overlayText?: readonly string[];
}

export type ConceptAdCritiqueResponse =
  | { state: 'none'; adUnitId: string }
  | { state: 'cached' | 'ready'; result: AdCritiqueResult; stale: boolean; adUnitId: string };

async function callCritique(
  accessToken: string,
  payload: Record<string, unknown>,
): Promise<ConceptAdCritiqueResponse> {
  const response = await fetch('/api/concepts/critique', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(payload),
  });
  const body = (await response.json().catch(() => null)) as
    (Partial<ConceptAdCritiqueResponse> & { error?: string }) | null;
  if (!response.ok) throw new Error(body?.error || `The critique failed (${response.status}).`);
  if ((body as { state?: string } | null)?.state === 'no_copy')
    return { state: 'none', adUnitId: '' };
  return body as ConceptAdCritiqueResponse;
}

/**
 * The Critique section's data. The cached read never runs a model; only
 * `run()` does, and its result replaces the cache. Both are keyed by Concept +
 * size + copy option, so switching size or copy option shows that pairing's own
 * result rather than carrying the previous one across.
 */
export function useConceptAdCritique(target: ConceptAdCritiqueTarget) {
  const { session, user } = useAuth();
  const { activeKit } = useBrandKits();
  const queryClient = useQueryClient();
  const brandKitId = activeKit?.id ?? null;
  const accessToken = session?.access_token ?? null;

  const payload = {
    conceptId: target.conceptId,
    brandKitId,
    renditionId: target.renditionId,
    adUnitId: target.adUnitId ?? null,
    mediaId: target.mediaId ?? null,
    overlayText: target.overlayText ?? [],
  };
  const key = [
    'concept-ad-critique',
    user?.id ?? 'anon',
    brandKitId ?? 'no-workspace',
    target.conceptId ?? 'none',
    target.renditionId ?? 'none',
    target.adUnitId ?? 'current',
    target.mediaId ?? 'no-image',
    (target.overlayText ?? []).join('\u0001'),
  ];
  const enabled = Boolean(accessToken && brandKitId && target.conceptId && target.renditionId);

  const cachedQuery = useQuery({
    queryKey: key,
    enabled,
    staleTime: 60 * 1000,
    retry: false,
    queryFn: () => callCritique(accessToken!, { ...payload, run: false }),
  });

  const inFlight = useRef(false);
  const runMutation = useMutation({
    mutationFn: () => {
      if (!accessToken) throw new Error('You must be signed in to critique an ad.');
      return callCritique(accessToken, { ...payload, run: true });
    },
    onSuccess: (response) => {
      queryClient.setQueryData(key, response);
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });

  return {
    response: cachedQuery.data ?? null,
    isLoading: cachedQuery.isPending && enabled,
    isRunning: runMutation.isPending,
    error: runMutation.error ?? cachedQuery.error,
    canRun: enabled,
    run: () => {
      if (inFlight.current) return;
      inFlight.current = true;
      runMutation.mutate();
    },
  };
}
